// 2D nesting visualization. layout: { bin_width, used_length, placements:[{x,y,w,h,label}], sheet_height?, guide_height?, sections_total? }
export default function NestingCanvas({ layout, unit = "in", thumbs = null, sectionInfo = null, fit = false, onFitChange = null }) {
  if (!layout || !layout.placements) return null;
  const { bin_width, used_length, placements, sheet_height, guide_height, min_band_in } = layout;
  const par = fit ? "xMidYMid meet" : "xMidYMid slice";   // Fit = whole art with margins; Fill = crop to fill
  const hasArt = !!(thumbs && placements.some((p) => (p.page != null ? thumbs[p.page] : thumbs[p.label])));
  const W = 320;
  const scale = W / (bin_width || 1);
  const GAP = sheet_height ? 12 : 0;                 // visual gap between fixed sections (px)
  const secH = sheet_height ? sheet_height * scale : 0;
  const secsDrawn = sheet_height ? Math.max(1, Math.round((used_length || sheet_height) / sheet_height)) : 0;
  const H = sheet_height
    ? Math.max(secsDrawn * secH + (secsDrawn - 1) * GAP + 2, 40)
    : Math.max((used_length || 1) * scale, 40);
  const colors = ["#2495D3", "#0EA5E9", "#38BDF8", "#0284C7", "#1E7AA9", "#7DD3FC"];
  // Map an inches Y coordinate to screen Y, inserting a visual gap between fixed sections.
  const sy = (y) => {
    if (!sheet_height) return y * scale;
    const s = Math.floor((y + 0.001) / sheet_height);
    const local = y - s * sheet_height;
    return s * (secH + GAP) + local * scale;
  };
  return (
    <div className="mt-2">
      <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-1">
        <span>Layout · {bin_width}" wide</span>
        {sheet_height
          ? null
          : (sectionInfo ? <span data-testid="nesting-section-info" className="normal-case font-semibold text-[#2495D3]">{sectionInfo}</span> : null)}
        <span className="flex items-center gap-2">
          {hasArt && (
            <span className="inline-flex rounded-md overflow-hidden border border-slate-200" data-testid="nesting-fit-toggle" title="Fill = crop art to fill each tile · Fit = show the whole sign with margins (preview only)">
              <button type="button" onClick={() => onFitChange && onFitChange(false)} className={`px-1.5 py-0.5 ${!fit ? "bg-[#2495D3] text-white" : "text-slate-500 hover:bg-slate-100"}`} data-testid="nesting-fit-fill">Fill</button>
              <button type="button" onClick={() => onFitChange && onFitChange(true)} className={`px-1.5 py-0.5 ${fit ? "bg-[#2495D3] text-white" : "text-slate-500 hover:bg-slate-100"}`} data-testid="nesting-fit-fit">Fit</button>
            </span>
          )}
          {used_length}" used
        </span>
      </div>
      <svg width={W} height={H} className="border border-slate-200 rounded-sm bg-slate-50" data-testid="nesting-canvas">
        {sheet_height && Array.from({ length: secsDrawn }).map((_, s) => {
          const top = s * (secH + GAP);
          const gY = guide_height ? top + guide_height * scale : null;
          return (
            <g key={`sec${s}`} data-testid={`nesting-section-box-${s}`}>
              <rect x="0.5" y={top + 0.5} width={W - 1} height={secH - 1} fill="#FFFFFF" stroke="#94A3B8" strokeWidth="1" rx="2" />
              {gY != null && gY < top + secH - 0.5 && (
                <g>
                  <line x1="0" y1={gY} x2={W} y2={gY} stroke="#CBD5E1" strokeWidth="1" strokeDasharray="3 3" />
                  <text x={W - 3} y={gY - 2} fontSize="7" textAnchor="end" fill="#94A3B8" className="font-mono">{Math.round(guide_height)}"</text>
                </g>
              )}
              {min_band_in != null && (top + min_band_in * scale) < top + secH - 0.5 && (
                <g>
                  <line x1="0" y1={top + min_band_in * scale} x2={W} y2={top + min_band_in * scale} stroke="#F59E0B" strokeWidth="1" strokeDasharray="4 2" />
                  <text x={W - 3} y={top + min_band_in * scale - 2} fontSize="7" textAnchor="end" fill="#D97706" className="font-mono">{Math.round(min_band_in)}" min</text>
                </g>
              )}
            </g>
          );
        })}
        {placements.map((p, i) => {
          const thumb = thumbs ? (p.page != null ? thumbs[p.page] : thumbs[p.label]) : null;
          const px = p.x * scale, py = sy(p.y), pw2 = p.w * scale, ph2 = p.h * scale;
          const pad = fit ? Math.min(pw2, ph2) * 0.16 : 0;   // Fit pulls the art in with a clear margin so it reads "zoomed out" vs Fill
          return (
            <g key={i}>
              {thumb ? (
                <>
                  {p.rot ? (
                    <image href={thumb} x={px + pw2 / 2 - ph2 / 2 + pad} y={py + ph2 / 2 - pw2 / 2 + pad} width={Math.max(1, ph2 - 2 * pad)} height={Math.max(1, pw2 - 2 * pad)} preserveAspectRatio={par} transform={`rotate(90 ${px + pw2 / 2} ${py + ph2 / 2})`} />
                  ) : (
                    <image href={thumb} x={px + pad} y={py + pad} width={Math.max(1, pw2 - 2 * pad)} height={Math.max(1, ph2 - 2 * pad)} preserveAspectRatio={par} />
                  )}
                  <rect x={px} y={py} width={pw2} height={ph2} fill="none" stroke={p.tiled ? "#D97706" : "#0A0A0A"} strokeOpacity={p.tiled ? 0.9 : 0.3} strokeWidth={p.tiled ? 1.5 : 1} strokeDasharray={p.tiled ? "5 3" : undefined} />
                </>
              ) : (
                <rect x={px} y={py} width={pw2} height={ph2}
                  fill={p.tiled ? "#F59E0B" : colors[i % colors.length]} fillOpacity={p.tiled ? 0.25 : 0.22}
                  stroke={p.tiled ? "#D97706" : colors[i % colors.length]} strokeWidth={p.tiled ? 1.5 : 1}
                  strokeDasharray={p.tiled ? "5 3" : undefined} />
              )}
              {pw2 > 14 && ph2 > 8 && (
                <text x={px + 2} y={py + Math.min(11, ph2 - 2)} fontSize={Math.max(6, Math.min(9, Math.floor(pw2 / 3)))} fill={thumb ? "#FFFFFF" : (p.tiled ? "#B45309" : "#0A0A0A")}
                  stroke={thumb ? "#0A0A0A" : "none"} strokeWidth={thumb ? 0.4 : 0} paintOrder="stroke" className="font-mono">
                  {p.label || `${p.w}×${p.h}`}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      {sheet_height && (
        <div className="text-[10px] text-slate-400 mt-1 font-mono">
          Each section {Math.round(bin_width)}"×{Math.round(sheet_height)}" · grey line = {Math.round(guide_height || 36)}" guide{min_band_in ? ` · amber line = ${Math.round(min_band_in)}" min charge` : ""}
        </div>
      )}
      {placements.some((p) => p.tiled) && (
        <div className="flex items-center gap-1.5 text-[10px] text-amber-600 mt-1 font-mono" data-testid="nesting-oversize-legend">
          <span className="inline-block w-3 h-3 rounded-sm" style={{ background: "#F59E0B", opacity: 0.4, border: "1.5px dashed #D97706" }} />
          Amber dashed = oversize section (seam)
        </div>
      )}
    </div>
  );
}
